<template>
  <div class="trendChart" :id="chartId"></div>
</template>

<script>
  import c3 from 'c3';
  import lodash from 'lodash';
  import { mapGetters } from 'vuex';
  import DataMethods from '../../utils/DataMethods.js';

  export default {
    name: 'TrendChart',
    mixins: [DataMethods],
    data() {
      return {
        chartId: `trendChart`,
        propertyName: '',
        label: '',
        curYearColor: '#824A9E',
        prevYearColor: '#FF584C',
        labelFormat: '$,.0f',
      };
    },
    computed: {
      ...mapGetters('audiologyKpis', [
        'month',
        'year',
        'yearStart',
        'curYear',
        'prevYear',
        'benchmarks',
        'metrics',
      ]),
      metric() {
        return lodash.find(this.metrics, ['field', this.propertyName]);
      },
      type() {
        return this.metric.type;
      },
      precision() {
        return this.metric.precision;
      },
      benchmark() {
        return lodash.find(this.benchmarks, ['kpi_name', this.propertyName]);
      },
      highBenchmark() {
        return this.benchmark !== undefined ? this.benchmark.high_value_monthly : 0;
      },
      medianBenchmark() {
        return this.benchmark !== undefined ? this.benchmark.median_value_monthly : 0;
      },
      lowBenchmark() {
        return this.benchmark !== undefined ? this.benchmark.low_value_monthly : 0;
      },
      highBenchmarkFormatted() {
        return this.formatData(this.highBenchmark);
      },
      medianBenchmarkFormatted() {
        return this.formatData(this.medianBenchmark);
      },
      lowBenchmarkFormatted() {
        return this.formatData(this.lowBenchmark);
      },
      chartIdString() {
        return `#${this.chartId}`;
      },
      longestDataSetSize() {
        return lodash.max([this.curYear.length, this.prevYear.length]);
      },
      monthArray() {
        return lodash.map([...Array(12)], (month, index) => this.getCurrentYearName(index + 1) + '-' + this.getMonthNumber(index + 1) + '-01');
      },
      curYearLabel() {
        return `${this.label} Current Yr`;
      },
      prevYearLabel() {
        return `${this.label} Prior Yr`;
      },
      prevYearValues() {
        return lodash.merge(
          lodash.fill(Array(12), '-'),
          lodash.map(this.prevYear, (value) => value[this.propertyName] !== '' ? value[this.propertyName] : "0"),
        );
      },
      curYearValues() {
        return lodash.merge(
          lodash.fill(Array(12), '-'),
          lodash.map(this.curYear, (value) => value[this.propertyName] !== '' ? value[this.propertyName] : "0"),
        );
      },
      chartData() {
        return {
          curYearValues: this.curYearValues,
          prevYearValues: this.prevYearValues,
          months: this.monthArray,
        };
      },
      chartDataJson() {
        const chartData = {
          months: this.chartData.months
        };
        chartData[this.curYearLabel] = this.curYearValues;
        chartData[this.prevYearLabel] = this.prevYearValues;
        return chartData;
      },
      allValues() {
        if (this.benchmark === undefined) {
          return lodash.concat(this.curYearValues, this.prevYearValues);
        }
        return lodash.concat(this.curYearValues, this.prevYearValues, this.highBenchmark, this.lowBenchmark);
      },
      maxValue() {
        return lodash.chain(this.allValues).map((value) => parseFloat(value)).max().value();
      },
      minValue() {
        return lodash.chain(this.allValues).map((value) => parseFloat(value)).min().value();
      },
      benchmarkLines() {
        return this.benchmark === undefined ? [] : [
          { value: this.lowBenchmark || 0, text: `Low (${this.lowBenchmarkFormatted})`, class: 'metric--benchmark-low' },
          { value: this.medianBenchmark || 0, text: `Median (${this.medianBenchmarkFormatted})`, class: 'metric--benchmark-median' },
          { value: this.highBenchmark || 0, text: `High (${this.highBenchmarkFormatted})`, class: 'metric--benchmark-high' },
        ];
      },
    },
    mounted() {
      window.addEventListener('resize', this.drawChart());
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.drawChart());
    },
    watch: {
      benchmark() {
        this.drawChart();
      },
    },
    methods: {
      /**
       *  Calculation Methods
       */
      formatData(value) {
        // Customize return value depending on metric
        if (this.type == 'money') {
          return this.currency(value, '$', this.precision);
        } else if (this.type == 'unit') {
          return this.currency(value, '', this.precision);
        } else if (this.type == 'percent') {
          return this.round(this.getPercent(value), this.precision) + '%';
        } else if (this.type == 'ratio') {
          return this.round(value, 2);
        }
        return value;
      },
      getCurrentYearName(month) {
        return (month) + this.yearStart > 13 ? parseInt(this.year) + 1 : parseInt(this.year);
      },
      getMonthNumber(month, pad = true) {
        let monthString = ((month + (this.yearStart - 2)) % 12) + 1;
        return (pad == true) ? monthString.toString().padStart(2, '0') : monthString;
      },
      drawChart() {
        const types = {};
        types[this.prevYearLabel] = 'line';
        const colors = {};
        colors[this.curYearLabel] = this.curYearColor;
        colors[this.prevYearLabel] = this.prevYearColor;
        //Draw chart
        c3.generate({
          bindto: this.chartIdString,
          data: {
            json: this.chartDataJson,
            x: 'months',
            type: 'bar',
            types: types,
            colors: colors,
          },
          axis: {
            x: {
              type: 'timeseries',
              tick: {format: '%b', culling: false},
            },
            y: {
              tick: {format: d3.format(this.labelFormat)},
              min: this.minValue > 0 ? this.minValue / 1.5 : this.minValue * 1.5,
              max: this.maxValue,
            },
          },
          grid: {
            y: {
              show: true,
              lines: this.benchmarkLines,
            },

          },
          tooltip: {
            format: {value: d3.format(this.labelFormat)}
          },
        });
      },
    },
  };
</script>
